import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        destinations: 'Destinations',
        aiConcierge: 'AI Concierge',
        map: 'Map',
        safety: 'Safety',
        myTrips: 'My Trips',
        signIn: 'Sign In',
        sos: 'SOS',
        profile: 'Profile',
      },
      // Hero
      hero: {
        badge: 'AI-Powered Travel Planning',
        title: 'Experience Morocco',
        subtitle: 'Like Never Before',
        description: 'Personalized itineraries, real-time safety info, and AI-powered recommendations for your unforgettable Moroccan adventure.',
        cta: 'Start Planning Free',
        chatCta: 'Chat with AI',
      },
      // Interests
      interests: {
        culture: 'Culture',
        food: 'Food',
        adventure: 'Adventure',
        photography: 'Photography',
      },
      // Stats
      stats: {
        travelers: 'Happy Travelers',
        rating: 'App Rating',
        guides: 'Verified Guides',
        cities: 'Cities Covered',
      },
      // Destinations
      destinations: {
        title: 'Top Destinations',
        heading: 'Where Will You',
        headingHighlight: 'Explore?',
        landmarks: 'landmarks',
        safe: 'Safe',
        caution: 'Caution',
      },
      // How it works
      howItWorks: {
        badge: 'How It Works',
        title: 'Three Steps to Your',
        titleHighlight: 'Dream Trip',
        step1Title: 'Tell us your dream',
        step1Desc: 'Share your interests, budget, and travel style with our AI concierge.',
        step2Title: 'AI creates your plan',
        step2Desc: 'Get a personalized day-by-day itinerary crafted in seconds.',
        step3Title: 'Explore confidently',
        step3Desc: 'Navigate with real-time safety info and local insights.',
      },
      // Features
      features: {
        badge: 'Key Features',
        title: 'Everything You Need for a',
        titleHighlight: 'Perfect Trip',
        aiConcierge: 'AI Concierge',
        aiConciergeDesc: 'GPT-powered personalized trip plans tailored to your interests, budget, and travel style.',
        interactiveMap: 'Interactive Map',
        interactiveMapDesc: 'Explore landmarks with real-time safety indicators and detailed information at your fingertips.',
        safetyDashboard: 'Safety Dashboard',
        safetyDashboardDesc: 'Real-time safety scores, emergency SOS, and instant access to local emergency services.',
        smartChat: 'Smart Chat',
        smartChatDesc: 'Ask anything about Morocco and get instant, knowledgeable responses from our AI assistant.',
      },
      // Testimonials
      testimonials: {
        badge: 'Testimonials',
        title: 'What Travelers',
        titleHighlight: 'Say',
      },
      // FAQ
      faq: {
        badge: 'FAQ',
        title: 'Frequently Asked',
        titleHighlight: 'Questions',
        q1: 'How does the AI itinerary planner work?',
        a1: 'Our AI analyzes your travel preferences, budget, interests, and travel dates to create a personalized day-by-day itinerary. It considers local events, weather, and opening hours to optimize your experience.',
        q2: 'Is MarocSphere free to use?',
        a2: 'Yes! Basic itinerary planning and safety features are completely free. Premium features like detailed offline maps and priority support are available with our Pro plan.',
        q3: 'How accurate is the safety information?',
        a3: 'Our safety data is updated in real-time from multiple sources including local authorities, traveler reports, and verified news sources. We refresh safety scores every 15 minutes.',
        q4: 'Can I customize my generated itinerary?',
        a4: 'Absolutely! After the AI generates your initial plan, you can easily swap activities, adjust timing, add or remove destinations, and save multiple versions.',
        q5: 'Does the app work offline?',
        a5: 'Yes, you can download your itinerary and maps for offline access. Safety alerts require an internet connection for real-time updates.',
      },
      // CTA
      cta: {
        badge: 'Get Started Today',
        title: 'Ready to Discover Morocco?',
        description: 'Join 50,000+ travelers who\'ve planned their perfect Moroccan adventure with MarocSphere. No credit card required.',
        button: 'Create Free Account',
        tryAi: 'Try AI Planner',
      },
      // Footer
      footer: {
        description: 'AI-powered travel planning for Morocco. Discover, plan, and explore the magic of the Kingdom with confidence.',
        explore: 'Explore',
        safety: 'Safety',
        contact: 'Contact',
        police: 'Police',
        ambulance: 'Ambulance',
        safetyDashboard: 'Safety Dashboard',
        rights: 'All rights reserved.',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
      },
      // Concierge
      concierge: {
        title: 'AI Travel Concierge',
        subtitle: 'Tell us about your dream Morocco trip',
        destination: 'Destination',
        selectDestination: 'Select a destination',
        duration: 'Duration (days)',
        travelerType: 'Traveler Type',
        solo: 'Solo',
        couple: 'Couple',
        family: 'Family',
        group: 'Group',
        interests: 'Interests',
        budget: 'Budget',
        budgetBudget: 'Budget',
        budgetMidrange: 'Mid-range',
        budgetLuxury: 'Luxury',
        startDate: 'Start Date',
        generate: 'Generate Itinerary',
        generating: 'Creating your perfect trip...',
        limitReached: 'Daily limit reached',
        limitMessage: 'Free plan: 1 itinerary per day. Upgrade for unlimited.',
      },
      // Chat
      chat: {
        title: 'AI Travel Assistant',
        placeholder: 'Ask me anything about Morocco...',
        send: 'Send',
        messagesLeft: 'messages left today',
        limitReached: 'Daily message limit reached',
        limitMessage: 'Free plan: 5 messages per day. Come back tomorrow!',
        suggestions: {
          safety: 'Is Morocco safe?',
          weather: 'Best time to visit?',
          food: 'Must-try dishes',
          tips: 'Travel tips',
        },
      },
      // Safety
      safety: {
        title: 'Safety Dashboard',
        score: 'Safety Score',
        status: 'Status',
        alerts: 'Active Alerts',
        tips: 'Safety Tips',
        emergency: 'Emergency Numbers',
        sos: 'Emergency SOS',
        sosDesc: 'Tap to alert emergency services with your location',
        sosConfirm: 'Are you sure you want to trigger emergency SOS?',
        sosTriggered: 'Emergency alert sent!',
      },
      // Trips
      trips: {
        title: 'My Trips',
        noTrips: 'No trips yet',
        createFirst: 'Create your first itinerary',
        planned: 'Planned',
        active: 'Active',
        completed: 'Completed',
        delete: 'Delete',
        view: 'View Details',
        limitReached: 'Trip limit reached',
        limitMessage: 'Free plan: 2 saved trips. Delete one to add more.',
      },
      // Auth
      auth: {
        signIn: 'Sign in',
        signUp: 'Create account',
        signInDesc: 'Continue your journey with MarocSphere',
        signUpDesc: 'Free forever. No credit card required.',
        email: 'Email',
        password: 'Password',
        name: 'Full Name',
        forgotPassword: 'Forgot password?',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        createOne: 'Create one',
        resetPassword: 'Reset password',
        resetDesc: "Enter your email and we'll send a reset link",
        sendResetLink: 'Send Reset Link',
        backToSignIn: 'Back to Sign In',
        checkEmail: 'Check your email',
        checkEmailDesc: "If an account exists for",
        checkEmailDesc2: ", we've sent a password reset link.",
        clickToReset: 'Click here to reset password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        setNewPassword: 'Set new password',
        setNewPasswordDesc: 'Choose a strong password for your account',
        resetPasswordBtn: 'Reset Password',
        passwordReset: 'Password Reset!',
        passwordResetDesc: 'Your password has been changed successfully.',
        invalidLink: 'Invalid Reset Link',
        invalidLinkDesc: 'This password reset link is invalid or has expired.',
        requestNew: 'Request a new reset link',
      },
      // Dashboard
      dashboard: {
        explorer: 'Explorer',
        reservations: 'Reservations',
        guides: 'Certified Guides',
        favorites: 'Favorites',
        messages: 'Messages',
        breadcrumb: 'Madina',
        aiItinerary: 'AI Itinerary',
        basedOnPrefs: 'Based on your preferences',
        viewItinerary: 'View itinerary',
        planYourTrip: 'Plan your trip',
        guidesAvailable: 'certified guides available',
        beforeTrip: 'before your trip',
        savedActivities: 'saved activities',
        avgRating: 'avg. guide rating',
        guidesNearYou: 'Guides near you',
        viewAll: 'View all',
        certified: 'certified',
        recommendedItinerary: 'Recommended itinerary',
        modify: 'Modify',
        day: 'Day',
        tourist: 'Tourist',
      },
      // Profile
      profile: {
        editProfile: 'Edit Profile',
        backToDashboard: 'Back to Dashboard',
        basicInfo: 'Basic Information',
        fullName: 'Full Name',
        phone: 'Phone',
        bio: 'Bio',
        bioPlaceholder: 'Tell us about yourself...',
        preferredLanguage: 'Preferred Language',
        avatarColor: 'Avatar Color',
        travelStyle: 'Travel Style',
        interests: 'Interests',
        saveProfile: 'Save Profile',
        adventure: 'Adventure',
        cultural: 'Cultural',
        relaxation: 'Relaxation',
        foodie: 'Foodie',
        photography: 'Photography',
        budget: 'Budget',
      },
      // Common
      common: {
        loading: 'Loading...',
        error: 'Something went wrong',
        retry: 'Try again',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        free: 'FREE',
        premium: 'PRO',
        upgrade: 'Upgrade',
      },
    },
  },
  fr: {
    translation: {
      // Navigation
      nav: {
        destinations: 'Destinations',
        aiConcierge: 'Concierge IA',
        map: 'Carte',
        safety: 'Sécurité',
        myTrips: 'Mes Voyages',
        signIn: 'Connexion',
        sos: 'SOS',
        profile: 'Profil',
      },
      // Hero
      hero: {
        badge: 'Planification de Voyage par IA',
        title: 'Découvrez le Maroc',
        subtitle: 'Comme Jamais',
        description: 'Itinéraires personnalisés, infos sécurité en temps réel et recommandations IA pour votre aventure marocaine inoubliable.',
        cta: 'Commencer Gratuitement',
        chatCta: 'Discuter avec l\'IA',
      },
      // Interests
      interests: {
        culture: 'Culture',
        food: 'Gastronomie',
        adventure: 'Aventure',
        photography: 'Photographie',
      },
      // Stats
      stats: {
        travelers: 'Voyageurs Heureux',
        rating: 'Note de l\'App',
        guides: 'Guides Vérifiés',
        cities: 'Villes Couvertes',
      },
      // Destinations
      destinations: {
        title: 'Top Destinations',
        heading: 'Où Allez-Vous',
        headingHighlight: 'Explorer?',
        landmarks: 'sites',
        safe: 'Sûr',
        caution: 'Prudence',
      },
      // How it works
      howItWorks: {
        badge: 'Comment Ça Marche',
        title: 'Trois Étapes Vers Votre',
        titleHighlight: 'Voyage de Rêve',
        step1Title: 'Partagez votre rêve',
        step1Desc: 'Partagez vos intérêts, budget et style de voyage avec notre concierge IA.',
        step2Title: 'L\'IA crée votre plan',
        step2Desc: 'Obtenez un itinéraire personnalisé jour par jour en quelques secondes.',
        step3Title: 'Explorez en confiance',
        step3Desc: 'Naviguez avec des infos sécurité en temps réel et des insights locaux.',
      },
      // Features
      features: {
        badge: 'Fonctionnalités',
        title: 'Tout Ce Dont Vous Avez Besoin pour un',
        titleHighlight: 'Voyage Parfait',
        aiConcierge: 'Concierge IA',
        aiConciergeDesc: 'Plans de voyage personnalisés par GPT adaptés à vos intérêts, budget et style.',
        interactiveMap: 'Carte Interactive',
        interactiveMapDesc: 'Explorez les sites avec des indicateurs de sécurité en temps réel et des infos détaillées.',
        safetyDashboard: 'Dashboard Sécurité',
        safetyDashboardDesc: 'Scores de sécurité en temps réel, SOS d\'urgence et accès aux services d\'urgence locaux.',
        smartChat: 'Chat Intelligent',
        smartChatDesc: 'Posez n\'importe quelle question sur le Maroc et obtenez des réponses instantanées de notre assistant IA.',
      },
      // Testimonials
      testimonials: {
        badge: 'Témoignages',
        title: 'Ce Que Disent les',
        titleHighlight: 'Voyageurs',
      },
      // FAQ
      faq: {
        badge: 'FAQ',
        title: 'Questions',
        titleHighlight: 'Fréquentes',
        q1: 'Comment fonctionne le planificateur d\'itinéraire IA?',
        a1: 'Notre IA analyse vos préférences de voyage, budget, intérêts et dates pour créer un itinéraire personnalisé jour par jour. Elle prend en compte les événements locaux, la météo et les horaires d\'ouverture.',
        q2: 'MarocSphere est-il gratuit?',
        a2: 'Oui! La planification d\'itinéraire de base et les fonctionnalités de sécurité sont entièrement gratuites. Les fonctionnalités premium sont disponibles avec notre plan Pro.',
        q3: 'Les informations de sécurité sont-elles fiables?',
        a3: 'Nos données de sécurité sont mises à jour en temps réel depuis plusieurs sources incluant les autorités locales et les rapports de voyageurs. Nous actualisons les scores toutes les 15 minutes.',
        q4: 'Puis-je personnaliser mon itinéraire généré?',
        a4: 'Absolument! Après la génération par l\'IA, vous pouvez facilement échanger des activités, ajuster les horaires, ajouter ou supprimer des destinations.',
        q5: 'L\'application fonctionne-t-elle hors ligne?',
        a5: 'Oui, vous pouvez télécharger votre itinéraire et cartes pour un accès hors ligne. Les alertes sécurité nécessitent une connexion internet.',
      },
      // CTA
      cta: {
        badge: 'Commencez Aujourd\'hui',
        title: 'Prêt à Découvrir le Maroc?',
        description: 'Rejoignez 50 000+ voyageurs qui ont planifié leur aventure marocaine parfaite avec MarocSphere. Aucune carte de crédit requise.',
        button: 'Créer un Compte Gratuit',
        tryAi: 'Essayer le Planificateur IA',
      },
      // Footer
      footer: {
        description: 'Planification de voyage par IA pour le Maroc. Découvrez, planifiez et explorez la magie du Royaume en toute confiance.',
        explore: 'Explorer',
        safety: 'Sécurité',
        contact: 'Contact',
        police: 'Police',
        ambulance: 'Ambulance',
        safetyDashboard: 'Dashboard Sécurité',
        rights: 'Tous droits réservés.',
        privacy: 'Politique de Confidentialité',
        terms: 'Conditions d\'Utilisation',
      },
      // Concierge
      concierge: {
        title: 'Concierge IA de Voyage',
        subtitle: 'Parlez-nous de votre voyage de rêve au Maroc',
        destination: 'Destination',
        selectDestination: 'Sélectionnez une destination',
        duration: 'Durée (jours)',
        travelerType: 'Type de Voyageur',
        solo: 'Solo',
        couple: 'Couple',
        family: 'Famille',
        group: 'Groupe',
        interests: 'Centres d\'Intérêt',
        budget: 'Budget',
        budgetBudget: 'Économique',
        budgetMidrange: 'Moyen',
        budgetLuxury: 'Luxe',
        startDate: 'Date de Début',
        generate: 'Générer l\'Itinéraire',
        generating: 'Création de votre voyage parfait...',
        limitReached: 'Limite quotidienne atteinte',
        limitMessage: 'Plan gratuit: 1 itinéraire par jour. Passez au Pro pour illimité.',
      },
      // Chat
      chat: {
        title: 'Assistant IA de Voyage',
        placeholder: 'Posez-moi n\'importe quelle question sur le Maroc...',
        send: 'Envoyer',
        messagesLeft: 'messages restants aujourd\'hui',
        limitReached: 'Limite quotidienne de messages atteinte',
        limitMessage: 'Plan gratuit: 5 messages par jour. Revenez demain!',
        suggestions: {
          safety: 'Le Maroc est-il sûr?',
          weather: 'Meilleure période?',
          food: 'Plats à essayer',
          tips: 'Conseils voyage',
        },
      },
      // Safety
      safety: {
        title: 'Dashboard Sécurité',
        score: 'Score de Sécurité',
        status: 'Statut',
        alerts: 'Alertes Actives',
        tips: 'Conseils de Sécurité',
        emergency: 'Numéros d\'Urgence',
        sos: 'SOS Urgence',
        sosDesc: 'Appuyez pour alerter les services d\'urgence avec votre position',
        sosConfirm: 'Êtes-vous sûr de vouloir déclencher le SOS d\'urgence?',
        sosTriggered: 'Alerte d\'urgence envoyée!',
      },
      // Trips
      trips: {
        title: 'Mes Voyages',
        noTrips: 'Pas encore de voyages',
        createFirst: 'Créez votre premier itinéraire',
        planned: 'Planifié',
        active: 'En Cours',
        completed: 'Terminé',
        delete: 'Supprimer',
        view: 'Voir Détails',
        limitReached: 'Limite de voyages atteinte',
        limitMessage: 'Plan gratuit: 2 voyages sauvegardés. Supprimez-en un pour en ajouter.',
      },
      // Auth
      auth: {
        signIn: 'Connexion',
        signUp: 'Creer un compte',
        signInDesc: 'Continuez votre voyage avec MarocSphere',
        signUpDesc: 'Gratuit pour toujours. Aucune carte requise.',
        email: 'Email',
        password: 'Mot de passe',
        name: 'Nom complet',
        forgotPassword: 'Mot de passe oublie?',
        noAccount: 'Pas encore de compte?',
        hasAccount: 'Deja un compte?',
        createOne: 'Creez-en un',
        resetPassword: 'Reinitialiser',
        resetDesc: 'Entrez votre email pour recevoir un lien',
        sendResetLink: 'Envoyer le lien',
        backToSignIn: 'Retour a la connexion',
        checkEmail: 'Verifiez votre email',
        checkEmailDesc: 'Si un compte existe pour',
        checkEmailDesc2: ', un lien a ete envoye.',
        clickToReset: 'Cliquez ici pour reinitialiser',
        newPassword: 'Nouveau mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
        setNewPassword: 'Nouveau mot de passe',
        setNewPasswordDesc: 'Choisissez un mot de passe securise',
        resetPasswordBtn: 'Reinitialiser',
        passwordReset: 'Mot de passe modifie!',
        passwordResetDesc: 'Votre mot de passe a ete change avec succes.',
        invalidLink: 'Lien invalide',
        invalidLinkDesc: 'Ce lien de reinitialisation est invalide ou a expire.',
        requestNew: 'Demander un nouveau lien',
      },
      // Dashboard
      dashboard: {
        explorer: 'Explorateur',
        reservations: 'Reservations',
        guides: 'Guides certifies',
        favorites: 'Favoris',
        messages: 'Messages',
        breadcrumb: 'Madina',
        aiItinerary: 'Itineraire IA',
        basedOnPrefs: 'Selon vos preferences',
        viewItinerary: 'Voir l\'itineraire',
        planYourTrip: 'Planifier votre voyage',
        guidesAvailable: 'guides certifies disponibles',
        beforeTrip: 'avant votre voyage',
        savedActivities: 'activites sauvegardees',
        avgRating: 'note moy. guides',
        guidesNearYou: 'Guides pres de vous',
        viewAll: 'Voir tous',
        certified: 'certifie',
        recommendedItinerary: 'Itineraire recommande',
        modify: 'Modifier',
        day: 'Jour',
        tourist: 'Touriste',
      },
      // Profile
      profile: {
        editProfile: 'Modifier le profil',
        backToDashboard: 'Retour au tableau de bord',
        basicInfo: 'Informations de base',
        fullName: 'Nom complet',
        phone: 'Telephone',
        bio: 'Bio',
        bioPlaceholder: 'Parlez-nous de vous...',
        preferredLanguage: 'Langue preferee',
        avatarColor: 'Couleur de l\'avatar',
        travelStyle: 'Style de voyage',
        interests: 'Centres d\'interet',
        saveProfile: 'Enregistrer le profil',
        adventure: 'Aventure',
        cultural: 'Culturel',
        relaxation: 'Detente',
        foodie: 'Gastronomie',
        photography: 'Photographie',
        budget: 'Economique',
      },
      // Common
      common: {
        loading: 'Chargement...',
        error: 'Une erreur est survenue',
        retry: 'Reessayer',
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        free: 'GRATUIT',
        premium: 'PRO',
        upgrade: 'Ameliorer',
      },
    },
  },
  ar: {
    translation: {
      // Navigation
      nav: {
        destinations: 'الوجهات',
        aiConcierge: 'مساعد الذكاء الاصطناعي',
        map: 'الخريطة',
        safety: 'السلامة',
        myTrips: 'رحلاتي',
        signIn: 'تسجيل الدخول',
        sos: 'طوارئ',
        profile: 'الملف الشخصي',
      },
      // Hero
      hero: {
        badge: 'تخطيط السفر بالذكاء الاصطناعي',
        title: 'اكتشف المغرب',
        subtitle: 'كما لم تره من قبل',
        description: 'برامج رحلات مخصصة، معلومات سلامة آنية، وتوصيات ذكية لمغامرتك المغربية التي لا تُنسى.',
        cta: 'ابدأ التخطيط مجاناً',
        chatCta: 'تحدث مع الذكاء الاصطناعي',
      },
      // Interests
      interests: {
        culture: 'ثقافة',
        food: 'طعام',
        adventure: 'مغامرة',
        photography: 'تصوير',
      },
      // Stats
      stats: {
        travelers: 'مسافر سعيد',
        rating: 'تقييم التطبيق',
        guides: 'مرشدين موثقين',
        cities: 'مدن مغطاة',
      },
      // Destinations
      destinations: {
        title: 'أفضل الوجهات',
        heading: 'أين ستذهب',
        headingHighlight: 'لتستكشف؟',
        landmarks: 'معالم',
        safe: 'آمن',
        caution: 'حذر',
      },
      // How it works
      howItWorks: {
        badge: 'كيف يعمل',
        title: 'ثلاث خطوات نحو',
        titleHighlight: 'رحلة أحلامك',
        step1Title: 'أخبرنا عن حلمك',
        step1Desc: 'شارك اهتماماتك وميزانيتك وأسلوب سفرك مع مساعدنا الذكي.',
        step2Title: 'الذكاء الاصطناعي يخطط لك',
        step2Desc: 'احصل على برنامج رحلة مخصص يومًا بيوم في ثوانٍ.',
        step3Title: 'استكشف بثقة',
        step3Desc: 'تنقل مع معلومات سلامة آنية ونصائح محلية.',
      },
      // Features
      features: {
        badge: 'المميزات',
        title: 'كل ما تحتاجه لـ',
        titleHighlight: 'رحلة مثالية',
        aiConcierge: 'مساعد ذكي',
        aiConciergeDesc: 'خطط سفر مخصصة بتقنية GPT مصممة حسب اهتماماتك وميزانيتك وأسلوبك.',
        interactiveMap: 'خريطة تفاعلية',
        interactiveMapDesc: 'استكشف المعالم مع مؤشرات سلامة آنية ومعلومات مفصلة.',
        safetyDashboard: 'لوحة السلامة',
        safetyDashboardDesc: 'درجات سلامة آنية، طوارئ SOS، ووصول فوري لخدمات الطوارئ المحلية.',
        smartChat: 'دردشة ذكية',
        smartChatDesc: 'اسأل أي شيء عن المغرب واحصل على إجابات فورية من مساعدنا الذكي.',
      },
      // Testimonials
      testimonials: {
        badge: 'شهادات',
        title: 'ماذا يقول',
        titleHighlight: 'المسافرون',
      },
      // FAQ
      faq: {
        badge: 'أسئلة شائعة',
        title: 'الأسئلة',
        titleHighlight: 'المتكررة',
        q1: 'كيف يعمل مخطط الرحلات بالذكاء الاصطناعي؟',
        a1: 'يحلل الذكاء الاصطناعي تفضيلات سفرك وميزانيتك واهتماماتك وتواريخك لإنشاء برنامج رحلة مخصص يومًا بيوم. يأخذ بعين الاعتبار الأحداث المحلية والطقس ومواعيد العمل.',
        q2: 'هل MarocSphere مجاني؟',
        a2: 'نعم! تخطيط الرحلات الأساسي وميزات السلامة مجانية تمامًا. الميزات المتقدمة متاحة مع خطتنا المدفوعة.',
        q3: 'هل معلومات السلامة موثوقة؟',
        a3: 'يتم تحديث بيانات السلامة لدينا آنيًا من مصادر متعددة تشمل السلطات المحلية وتقارير المسافرين. نحدث درجات السلامة كل 15 دقيقة.',
        q4: 'هل يمكنني تخصيص برنامج رحلتي؟',
        a4: 'بالتأكيد! بعد إنشاء الذكاء الاصطناعي لخطتك الأولية، يمكنك بسهولة تبديل الأنشطة وتعديل التوقيت وإضافة أو إزالة الوجهات.',
        q5: 'هل يعمل التطبيق بدون إنترنت؟',
        a5: 'نعم، يمكنك تحميل برنامج رحلتك والخرائط للوصول بدون إنترنت. تنبيهات السلامة تحتاج اتصال إنترنت للتحديثات الآنية.',
      },
      // CTA
      cta: {
        badge: 'ابدأ اليوم',
        title: 'مستعد لاكتشاف المغرب؟',
        description: 'انضم إلى +50,000 مسافر خططوا لمغامرتهم المغربية المثالية مع MarocSphere. لا حاجة لبطاقة ائتمان.',
        button: 'إنشاء حساب مجاني',
        tryAi: 'جرب المخطط الذكي',
      },
      // Footer
      footer: {
        description: 'تخطيط السفر بالذكاء الاصطناعي للمغرب. اكتشف، خطط، واستكشف سحر المملكة بثقة.',
        explore: 'استكشف',
        safety: 'السلامة',
        contact: 'تواصل',
        police: 'الشرطة',
        ambulance: 'الإسعاف',
        safetyDashboard: 'لوحة السلامة',
        rights: 'جميع الحقوق محفوظة.',
        privacy: 'سياسة الخصوصية',
        terms: 'شروط الخدمة',
      },
      // Concierge
      concierge: {
        title: 'مساعد السفر الذكي',
        subtitle: 'أخبرنا عن رحلتك المثالية للمغرب',
        destination: 'الوجهة',
        selectDestination: 'اختر وجهة',
        duration: 'المدة (أيام)',
        travelerType: 'نوع المسافر',
        solo: 'منفرد',
        couple: 'زوجان',
        family: 'عائلة',
        group: 'مجموعة',
        interests: 'الاهتمامات',
        budget: 'الميزانية',
        budgetBudget: 'اقتصادي',
        budgetMidrange: 'متوسط',
        budgetLuxury: 'فاخر',
        startDate: 'تاريخ البدء',
        generate: 'إنشاء البرنامج',
        generating: 'جاري إنشاء رحلتك المثالية...',
        limitReached: 'تم الوصول للحد اليومي',
        limitMessage: 'الخطة المجانية: برنامج واحد يومياً. قم بالترقية للحصول على غير محدود.',
      },
      // Chat
      chat: {
        title: 'مساعد السفر الذكي',
        placeholder: 'اسألني أي شيء عن المغرب...',
        send: 'إرسال',
        messagesLeft: 'رسائل متبقية اليوم',
        limitReached: 'تم الوصول للحد اليومي للرسائل',
        limitMessage: 'الخطة المجانية: 5 رسائل يومياً. عد غداً!',
        suggestions: {
          safety: 'هل المغرب آمن؟',
          weather: 'أفضل وقت للزيارة؟',
          food: 'أطباق يجب تجربتها',
          tips: 'نصائح السفر',
        },
      },
      // Safety
      safety: {
        title: 'لوحة السلامة',
        score: 'درجة السلامة',
        status: 'الحالة',
        alerts: 'التنبيهات النشطة',
        tips: 'نصائح السلامة',
        emergency: 'أرقام الطوارئ',
        sos: 'طوارئ SOS',
        sosDesc: 'اضغط لتنبيه خدمات الطوارئ بموقعك',
        sosConfirm: 'هل أنت متأكد من تفعيل طوارئ SOS؟',
        sosTriggered: 'تم إرسال تنبيه الطوارئ!',
      },
      // Trips
      trips: {
        title: 'رحلاتي',
        noTrips: 'لا توجد رحلات بعد',
        createFirst: 'أنشئ برنامج رحلتك الأول',
        planned: 'مخطط',
        active: 'نشط',
        completed: 'مكتمل',
        delete: 'حذف',
        view: 'عرض التفاصيل',
        limitReached: 'تم الوصول لحد الرحلات',
        limitMessage: 'الخطة المجانية: رحلتان محفوظتان. احذف واحدة لإضافة المزيد.',
      },
      // Auth
      auth: {
        signIn: '\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644',
        signUp: '\u0625\u0646\u0634\u0627\u0621 \u062d\u0633\u0627\u0628',
        signInDesc: '\u062a\u0627\u0628\u0639 \u0631\u062d\u0644\u062a\u0643 \u0645\u0639 \u0645\u0627\u0631\u0648\u0643\u0633\u0641\u064a\u0631',
        signUpDesc: '\u0645\u062c\u0627\u0646\u064a \u0644\u0644\u0623\u0628\u062f. \u0644\u0627 \u062d\u0627\u062c\u0629 \u0644\u0628\u0637\u0627\u0642\u0629 \u0627\u0626\u062a\u0645\u0627\u0646.',
        email: '\u0627\u0644\u0628\u0631\u064a\u062f \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a',
        password: '\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631',
        name: '\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0643\u0627\u0645\u0644',
        forgotPassword: '\u0646\u0633\u064a\u062a \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631\u061f',
        noAccount: '\u0644\u064a\u0633 \u0644\u062f\u064a\u0643 \u062d\u0633\u0627\u0628\u061f',
        hasAccount: '\u0644\u062f\u064a\u0643 \u062d\u0633\u0627\u0628 \u0628\u0627\u0644\u0641\u0639\u0644\u061f',
        createOne: '\u0623\u0646\u0634\u0626 \u0648\u0627\u062d\u062f\u0627',
        resetPassword: '\u0625\u0639\u0627\u062f\u0629 \u062a\u0639\u064a\u064a\u0646',
        resetDesc: '\u0623\u062f\u062e\u0644 \u0628\u0631\u064a\u062f\u0643 \u0648\u0633\u0646\u0631\u0633\u0644 \u0631\u0627\u0628\u0637 \u0625\u0639\u0627\u062f\u0629 \u0627\u0644\u062a\u0639\u064a\u064a\u0646',
        sendResetLink: '\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0631\u0627\u0628\u0637',
        backToSignIn: '\u0627\u0644\u0639\u0648\u062f\u0629 \u0644\u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u062f\u062e\u0648\u0644',
        checkEmail: '\u062a\u062d\u0642\u0642 \u0645\u0646 \u0628\u0631\u064a\u062f\u0643',
        checkEmailDesc: '\u0625\u0630\u0627 \u0643\u0627\u0646 \u0647\u0646\u0627\u0643 \u062d\u0633\u0627\u0628 \u0644\u0640',
        checkEmailDesc2: '\u0641\u0642\u062f \u0623\u0631\u0633\u0644\u0646\u0627 \u0631\u0627\u0628\u0637 \u0625\u0639\u0627\u062f\u0629 \u0627\u0644\u062a\u0639\u064a\u064a\u0646.',
        clickToReset: '\u0627\u0646\u0642\u0631 \u0647\u0646\u0627 \u0644\u0625\u0639\u0627\u062f\u0629 \u0627\u0644\u062a\u0639\u064a\u064a\u0646',
        newPassword: '\u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631 \u062c\u062f\u064a\u062f\u0629',
        confirmPassword: '\u062a\u0623\u0643\u064a\u062f \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631',
        setNewPassword: '\u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631 \u062c\u062f\u064a\u062f\u0629',
        setNewPasswordDesc: '\u0627\u062e\u062a\u0631 \u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631 \u0622\u0645\u0646\u0629 \u0644\u062d\u0633\u0627\u0628\u0643',
        resetPasswordBtn: '\u0625\u0639\u0627\u062f\u0629 \u0627\u0644\u062a\u0639\u064a\u064a\u0646',
        passwordReset: '\u062a\u0645 \u062a\u063a\u064a\u064a\u0631 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631!',
        passwordResetDesc: '\u062a\u0645 \u062a\u063a\u064a\u064a\u0631 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0628\u0646\u062c\u0627\u062d.',
        invalidLink: '\u0631\u0627\u0628\u0637 \u063a\u064a\u0631 \u0635\u0627\u0644\u062d',
        invalidLinkDesc: '\u0631\u0627\u0628\u0637 \u0625\u0639\u0627\u062f\u0629 \u0627\u0644\u062a\u0639\u064a\u064a\u0646 \u063a\u064a\u0631 \u0635\u0627\u0644\u062d \u0623\u0648 \u0645\u0646\u062a\u0647\u064a \u0627\u0644\u0635\u0644\u0627\u062d\u064a\u0629.',
        requestNew: '\u0637\u0644\u0628 \u0631\u0627\u0628\u0637 \u062c\u062f\u064a\u062f',
      },
      // Dashboard
      dashboard: {
        explorer: '\u0627\u0644\u0645\u0633\u062a\u0643\u0634\u0641',
        reservations: '\u0627\u0644\u062d\u062c\u0648\u0632\u0627\u062a',
        guides: '\u0627\u0644\u0645\u0631\u0634\u062f\u064a\u0646 \u0627\u0644\u0645\u0639\u062a\u0645\u062f\u064a\u0646',
        favorites: '\u0627\u0644\u0645\u0641\u0636\u0644\u0629',
        messages: '\u0627\u0644\u0631\u0633\u0627\u0626\u0644',
        breadcrumb: '\u0627\u0644\u0645\u062f\u064a\u0646\u0629',
        aiItinerary: '\u0628\u0631\u0646\u0627\u0645\u062c \u0630\u0643\u064a',
        basedOnPrefs: '\u062d\u0633\u0628 \u062a\u0641\u0636\u064a\u0644\u0627\u062a\u0643',
        viewItinerary: '\u0639\u0631\u0636 \u0627\u0644\u0628\u0631\u0646\u0627\u0645\u062c',
        planYourTrip: '\u062e\u0637\u0637 \u0631\u062d\u0644\u062a\u0643',
        guidesAvailable: '\u0645\u0631\u0634\u062f \u0645\u0639\u062a\u0645\u062f \u0645\u062a\u0627\u062d',
        beforeTrip: '\u0642\u0628\u0644 \u0631\u062d\u0644\u062a\u0643',
        savedActivities: '\u0623\u0646\u0634\u0637\u0629 \u0645\u062d\u0641\u0648\u0638\u0629',
        avgRating: '\u0645\u062a\u0648\u0633\u0637 \u062a\u0642\u064a\u064a\u0645 \u0627\u0644\u0645\u0631\u0634\u062f\u064a\u0646',
        guidesNearYou: '\u0645\u0631\u0634\u062f\u064a\u0646 \u0642\u0631\u064a\u0628\u064a\u0646 \u0645\u0646\u0643',
        viewAll: '\u0639\u0631\u0636 \u0627\u0644\u0643\u0644',
        certified: '\u0645\u0639\u062a\u0645\u062f',
        recommendedItinerary: '\u0628\u0631\u0646\u0627\u0645\u062c \u0645\u0648\u0635\u0649 \u0628\u0647',
        modify: '\u062a\u0639\u062f\u064a\u0644',
        day: '\u0627\u0644\u064a\u0648\u0645',
        tourist: '\u0633\u0627\u0626\u062d',
      },
      // Profile
      profile: {
        editProfile: '\u062a\u0639\u062f\u064a\u0644 \u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u0634\u062e\u0635\u064a',
        backToDashboard: '\u0627\u0644\u0639\u0648\u062f\u0629 \u0644\u0644\u0648\u062d\u0629',
        basicInfo: '\u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062a \u0627\u0644\u0623\u0633\u0627\u0633\u064a\u0629',
        fullName: '\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0643\u0627\u0645\u0644',
        phone: '\u0627\u0644\u0647\u0627\u062a\u0641',
        bio: '\u0627\u0644\u0633\u064a\u0631\u0629 \u0627\u0644\u0630\u0627\u062a\u064a\u0629',
        bioPlaceholder: '\u0623\u062e\u0628\u0631\u0646\u0627 \u0639\u0646 \u0646\u0641\u0633\u0643...',
        preferredLanguage: '\u0627\u0644\u0644\u063a\u0629 \u0627\u0644\u0645\u0641\u0636\u0644\u0629',
        avatarColor: '\u0644\u0648\u0646 \u0627\u0644\u0635\u0648\u0631\u0629',
        travelStyle: '\u0623\u0633\u0644\u0648\u0628 \u0627\u0644\u0633\u0641\u0631',
        interests: '\u0627\u0644\u0627\u0647\u062a\u0645\u0627\u0645\u0627\u062a',
        saveProfile: '\u062d\u0641\u0638 \u0627\u0644\u0645\u0644\u0641',
        adventure: '\u0645\u063a\u0627\u0645\u0631\u0629',
        cultural: '\u062b\u0642\u0627\u0641\u064a',
        relaxation: '\u0627\u0633\u062a\u0631\u062e\u0627\u0621',
        foodie: '\u0637\u0639\u0627\u0645',
        photography: '\u062a\u0635\u0648\u064a\u0631',
        budget: '\u0627\u0642\u062a\u0635\u0627\u062f\u064a',
      },
      // Common
      common: {
        loading: '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...',
        error: '\u062d\u062f\u062b \u062e\u0637\u0623 \u0645\u0627',
        retry: '\u062d\u0627\u0648\u0644 \u0645\u062c\u062f\u062f\u0627\u064b',
        save: '\u062d\u0641\u0638',
        cancel: '\u0625\u0644\u063a\u0627\u0621',
        delete: '\u062d\u0630\u0641',
        edit: '\u062a\u0639\u062f\u064a\u0644',
        free: '\u0645\u062c\u0627\u0646\u064a',
        premium: '\u0645\u062f\u0641\u0648\u0639',
        upgrade: '\u062a\u0631\u0642\u064a\u0629',
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  });

export default i18n;
